import { useEffect, useState, useRef } from "react";
import { Formik, Field, Form, ErrorMessage } from "formik";
import * as Yup from "yup";
import { useNavigate } from "react-router-dom";
import FileInput from "../../components/ui/FileInput";
import { API_URL, APP_CURRENCY } from "../../utilities/constants";
import ButtonTextOnly from "../../components/ui/ButtonTextOnly";
import FullPageLoader from "../../components/ui/FullPageLoader";
import PageTitle from "../../components/common/PageTitle";
import { useUI } from "../../context/ui";
import { CiCircleCheck, CiLocationArrow1 } from "react-icons/ci";
import LoaderIcon from "../../components/ui/LoaderIcon";
import RSTabs from "../../components/ui/RSTabs";
import WalletTopUp from "./WalletTopUp";
import ButtonTextIcon from "../../components/ui/ButtonTextIcon";
import { decodeRolesFromToken, handleApiCall } from "../../utilities/helpers";
import DragFileUpload from "../../components/ui/DragFileUpload";
import { HiArrowSmRight } from "react-icons/hi";

const AdminWallet = () => {
	const [userId, setUserId] = useState(null);
	const [loading, setLoading] = useState(false);
	const [uploadedURL, setUploadedURL] = useState("");
	const [activeTab, setActiveTab] = useState("adminWallet");
	const navigate = useNavigate();
	const token = localStorage.getItem("token");
	const fileInputRef = useRef(null);
	const {
		setNewNotification,
		setShowNotification,
		addNotification,
		setSegmentTitle,
		addToastMessage,
	} = useUI();

	const userRole = decodeRolesFromToken()[0];

	const ValidationSchema = Yup.object().shape({
		referenceId: Yup.string().required(
			"Wallet Account Number is required!"
		),
		transactionReceipt: Yup.string().required(
			"Transaction receipt is required!"
		),
		amount: Yup.string()
			.matches(/^\d+(\.\d{1,2})?$/, "Amount must be a valid number with up to two decimal places.")
			.test("not-zero", "Amount must be greater than 0.", (value) => parseFloat(value) > 0)
			.required("Amount is required"),
});

	const initialValues = {
		amount: "",
		transactionReceipt: "",
		referenceId: "",
	};

	const fetchUserId = async (referenceId) => {
		try {
			const response = await handleApiCall(
				`${API_URL}/ocpl/wallet/${referenceId}`
			);
			if (response.responseStatus == true) {
				setUserId(response.data.userId);
				return response.data.userId;
			}
			addToastMessage({
			status: false,
			message: response?.responseDescription ||`Something went wrong while deleting procedure. Please try again.`,
			});
			return false;
		} catch (error) {
			addToastMessage({
			status: false,
			message: `Something went wrong while deleting procedure. Please try again.`,
			});
			return false;
		}
	};

	const handleWallet = async (values, { resetForm }) => {
		setLoading(true);
		try {
			const userId = await fetchUserId(values.referenceId);
	
			if (!userId) {
				setNewNotification(true);
				addNotification({
					id: Date.now(),
					message: "Looks like you entered an incorrect account number.",
					status: false,
					archive: false,
				});
				return;
			}
	
			const request = {
				amount: values.amount,
				type: "C",
				currency: APP_CURRENCY,
				fromReference: "Gasolina wallet credit amount",
				toReference: "User txn ref number",
				userId,
				transactionReceipt: values.transactionReceipt || "/uploads/data/receipt.pdf",
			};
	
			const response = await fetch(`${API_URL}/ocpl/wallet/admin/txn`, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					"X-JWT-Assertion": token,
				},
				body: JSON.stringify(request),
			});
	
			if (response.ok) {
				addToastMessage({
					status: true,
					message: "Your wallet topup is successfully completed, thank you!",
				});
				resetForm();
				setNewNotification(true);
				addNotification({
					id: Date.now(),
					message: "Your wallet topup was successfully completed!",
					status: true,
					archive: false,
				});
	
				// Clear the file input and reset the related state values
				setUploadedURL("");
				if (fileInputRef.current) {
					fileInputRef.current.resetFile();
				}
			} else {
				const errorData = await response.json();
				const errorMessage = errorData.responseDescription;
				addToastMessage({
					status: false,
					message: errorMessage || "Something went wrong while performing topup. Please try again!",
				});
			}
		} catch (error) {
			setNewNotification(true);
			setShowNotification(true);
			addNotification({
				id: Date.now(),
				message: "Something went wrong while creating a Wallet.",
				status: false,
				archive: false,
			});
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		setSegmentTitle("Wallet");
	}, [setSegmentTitle]);

	const tabOptions = [
		{
			id: "adminWallet",
			title: "Customer Wallet Topup",
			content: (
				<section className='create-station'>
					<h5 className='color-primary'>Wallet Details</h5>
					<p>Enter the wallet details below:</p>
					<br />
					<section className='table-wrapper'>
						<Formik
							initialValues={initialValues}
							validationSchema={ValidationSchema}
							onSubmit={handleWallet}>
							{({
								errors,
								touched,
								setFieldValue,
								resetForm,
								handleSubmit,
								setFieldTouched,
								validateForm,
							}) => (
								<Form>
									<table>
										<tbody>
											<tr>
												<td colSpan='6'>
													<p className='title f-w-700 color-primary'>
														Amount in {APP_CURRENCY}
													</p>
													<Field
														name='amount'
														maxLength=""
														type='text'
														placeholder='Eg: 1200'
														className={`col-1-1 ${errors.amount &&
															touched.amount
															? "error"
															: ""
															}`}
													/>
													{errors.amount && <p className='error-messages' >{errors.amount}
													</p>  }
												</td>
											</tr>

											<tr>
												<td colSpan='6'>
													<p className='title f-w-700 color-primary'>
														Proof of Transaction
													</p>
													<DragFileUpload
														ref={fileInputRef}
														fileType="TRANSACTION_RECEIPT"
														error={errors.transactionReceipt && touched.transactionReceipt}
														afterUpload={(url) => {
															setFieldValue("transactionReceipt", url);
														}}
														setTouched={() => setFieldTouched("transactionReceipt", true)}
													/>
													<ErrorMessage
														name="transactionReceipt"
														component="p"
														className="error-messages"
													/>
												</td>
											</tr>


											{/* <tr>
												<td colSpan='4'>
													<p className='title f-w-700 color-primary'>
														Proof of Transaction
													</p>
													<FileInput
														defaultText='Transaction screenshot'
														accept='image/jpeg, image/jpg, image/png, application/pdf'
														callback={setImage}
													/>
												</td>
												<td
													colSpan='2'
													className='t-a-r'>
													<p>&nbsp;</p>
													<ButtonTextIcon
														type='button'
														title={
															uploadedURL
																? "Uploaded"
																: "Upload"
														}
														disabled={
															loading || !image
														}
														icon={
															loading ? (
																<LoaderIcon />
															) : uploadedURL ? (
																<CiCircleCheck />
															) : (
																<CiLocationArrow1 />
															)
														}
														onClick={handleUpload}
													/>
												</td>
											</tr>
												{image && (
													<tr>
														<td colSpan='2'>
															<p
																className='cursor-pointer'
																style={{
																	fontSize: "12px",
																	color: "red",
																}}
																onClick={() => {
																	setImage(null);
																	setUploadedURL(
																		""
																	);
																}}>
																Delete transaction receipt file
															</p>
														</td>
													</tr>

												)} */}
											<tr>
												<td colSpan="6" >
													<p className="status f-w-200 l-h-1 font-size-12">
														NOTE: Please upload file in JPG, JPEG, PNG or PDF format.
													</p>
												</td>
											</tr>
											<tr>
												<td colSpan='6'>
													<p className='title f-w-700 color-primary'>
														Account Number
													</p>
													<Field
														name='referenceId'
														type='text'
														placeholder='Eg: GUAXXXXXXXXXX'
														className={`col-1-1 ${errors.referenceId &&
															touched.referenceId
															? "error"
															: ""
															}`}
													/>
													<ErrorMessage
														name='referenceId'
														component='p'
														className='error-messages'
													/>
												</td>
											</tr>
											<tr>
												<td
													colSpan='6'
													className='btns t-a-r'>
													<ButtonTextIcon
														type='submit'
														title='Submit'
														icon={
															loading ? (
																<LoaderIcon />
															) : (
																<HiArrowSmRight />
															)
														}
														onClick={async () => {
															const errors = await validateForm();
															if (Object.keys(errors).length > 0) {
															  const firstErrorMessage = Object.values(errors)[0];
															  addToastMessage({
																status: false,
																message: `${firstErrorMessage || "Something went wrong, please try again later."}`,
															  });
															}
															handleSubmit();
														  }}
														disabled={
															loading 
														}
													/>
												</td>
											</tr>
										</tbody>
									</table>
								</Form>
							)}
						</Formik>
					</section>
				</section>
			),
		},
		...(userRole === 1
			? [
				{
				  id: "topup",
				  title: "Super Wallet Topup",
				  content: <WalletTopUp />,
				},
			  ]
			: []),
	];

	return (
		<section>
			<PageTitle title='Wallet' />
			<FullPageLoader isActive={loading} />
			<div className='pad-20'>
				<RSTabs
					options={tabOptions}
					activeTab={activeTab}
					onChange={setActiveTab}
				/>
			</div>
		</section>
	);
};
export default AdminWallet;
